import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    FiCheckCircle,
    FiClock,
    FiCopy,
    FiCreditCard,
    FiInfo,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiXCircle,
    FiZap,
} from "react-icons/fi";

const QUICK_AMOUNTS = [5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000];

const RechargeWallet = () => {
    // Resolve logged-in tenant ID & slug (tenant can only see their own wallet)
    const tenantInfo = useMemo(() => {
        try {
            const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
            if (vendorData?.tenant_id) {
                return {
                    id: Number(vendorData.tenant_id),
                    slug: String(vendorData.slug || vendorData.tenant_id),
                    name: vendorData.tenant_name || `Tenant #${vendorData.tenant_id}`,
                };
            }
        } catch (e) {
            // Ignore
        }
        // Fallback for standalone/testing
        const urlParams = new URLSearchParams(window.location.search);
        const queryId = urlParams.get("tenant_id") || 65;
        return {
            id: Number(queryId),
            slug: String(queryId),
            name: `Tenant #${queryId}`,
        };
    }, []);

    const selectedTenantId = tenantInfo.id;
    const selectedTenantSlug = tenantInfo.slug;
    const [tenantName, setTenantName] = useState(tenantInfo.name);

    // Wallet balance & details
    const [currentBalance, setCurrentBalance] = useState(0);
    const [thresholdBalance, setThresholdBalance] = useState(null);
    const [walletLoading, setWalletLoading] = useState(true);

    // Recharge Form State
    const [rechargeAmount, setRechargeAmount] = useState("");
    const [remarks, setRemarks] = useState("");
    const [rechargeSubmitting, setRechargeSubmitting] = useState(false);

    // Recharge Requests History State
    const [activeTab, setActiveTab] = useState("all"); // 'all' | 'pending' | 'resolved'
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPreviousPage: false,
    });

    // Summary calculations
    const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
    const [resolvedRequestsCount, setResolvedRequestsCount] = useState(0);
    const [rejectedRequestsCount, setRejectedRequestsCount] = useState(0);

    const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";
    const token = sessionStorage.getItem("access_token");

    // Initial fetch for the tenant's wallet only
    useEffect(() => {
        fetchTenantWallet();
        fetchHistory(1);
    }, [selectedTenantId]);

    // 1. Fetch Current Wallet Balance (restricted to tenant's own wallet via slug header)
    // Taking reference from: curl 'http://localhost:5000/api/admin/tenant-wallet'
    const fetchTenantWallet = async () => {
        setWalletLoading(true);
        try {
            const response = await axios({
                method: "get",
                url: `${baseUrl}/admin/tenant-wallet`,
                headers: {
                    Authorization: "Bearer " + token,
                    slug: String(selectedTenantSlug || selectedTenantId),
                    Accept: "application/json, text/plain, */*",
                },
            });

            if (response.data?.success && Array.isArray(response.data.body)) {
                const foundWallet = response.data.body.find(
                    (w) => Number(w.tenant_id) === Number(selectedTenantId)
                );

                if (foundWallet) {
                    setCurrentBalance(Number(foundWallet.wallet_balance) || 0);
                    setThresholdBalance(
                        foundWallet.threshold_balance != null
                            ? Number(foundWallet.threshold_balance)
                            : null
                    );
                    if (foundWallet.tenant_name) {
                        setTenantName(foundWallet.tenant_name);
                    }
                } else {
                    // Try direct fetch by ID if not in array
                    fetchWalletDirectly();
                }
            } else {
                fetchWalletDirectly();
            }
        } catch (error) {
            console.error("Error fetching wallet balance:", error);
            fetchWalletDirectly();
        } finally {
            setWalletLoading(false);
        }
    };

    const fetchWalletDirectly = async () => {
        try {
            const res = await axios({
                method: "get",
                url: `${baseUrl}/admin/tenant-wallet/${selectedTenantId}`,
                headers: {
                    Authorization: "Bearer " + token,
                    Accept: "application/json, text/plain, */*",
                },
            });
            if (res.data?.success && res.data.body) {
                setCurrentBalance(Number(res.data.body.wallet_balance) || 0);
                setThresholdBalance(
                    res.data.body.threshold_balance != null
                        ? Number(res.data.body.threshold_balance)
                        : null
                );
                if (res.data.body.tenant_name) {
                    setTenantName(res.data.body.tenant_name);
                }
            } else {
                // Default balance is 0 for newly initialized wallet
                setCurrentBalance(0);
            }
        } catch (e) {
            setCurrentBalance(0);
        }
    };

    // 2. Fetch Recharge Requests History (restricted strictly to recharge_request transaction_type)
    const fetchHistory = async (page = 1) => {
        setHistoryLoading(true);
        try {
            const params = {
                page,
                limit: pagination.limit || 10,
                transaction_type: "recharge_request",
            };

            const response = await axios({
                method: "get",
                url: `${baseUrl}/admin/tenant-wallet/${selectedTenantId}/passbook`,
                headers: {
                    Authorization: "Bearer " + token,
                    slug: String(selectedTenantSlug || selectedTenantId),
                    Accept: "application/json, text/plain, */*",
                },
                params,
            });

            if (response.data?.success) {
                const list = response.data.body?.data || [];
                setHistoryData(list);
                if (response.data.body?.pagination) {
                    setPagination(response.data.body.pagination);
                }

                // Calculate pending, resolved, and rejected counts
                let pending = 0;
                let resolved = 0;
                let rejected = 0;
                list.forEach((item) => {
                    const st = (item.status || "pending").toLowerCase();
                    if (st === "approved" || st === "resolved") {
                        resolved += 1;
                    } else if (st === "rejected" || st === "declined") {
                        rejected += 1;
                    } else {
                        pending += 1;
                    }
                });
                setPendingRequestsCount(pending);
                setResolvedRequestsCount(resolved);
                setRejectedRequestsCount(rejected);
            } else {
                toast.error(response.data?.message || "Failed to fetch recharge requests");
            }
        } catch (error) {
            console.error("Error fetching recharge requests:", error);
            toast.error(error.response?.data?.message || "Failed to fetch recharge requests");
        } finally {
            setHistoryLoading(false);
        }
    };

    // 3. Handle Create Recharge Request
    // Creates a recharge entry in wallet_passbook with transaction_type as 'recharge_request'
    const handleRechargeSubmit = async (e) => {
        e?.preventDefault();
        const amountNum = Number(rechargeAmount);

        if (!amountNum || Number.isNaN(amountNum) || amountNum <= 0) {
            toast.error("Please enter a valid amount greater than ₹0");
            return;
        }

        setRechargeSubmitting(true);
        const toastId = toast.loading("Submitting recharge request...");

        try {
            const response = await axios({
                method: "post",
                url: `${baseUrl}/admin/tenant-wallet/${selectedTenantId}/recharge`,
                headers: {
                    Authorization: "Bearer " + token,
                    slug: String(selectedTenantSlug || selectedTenantId),
                    "Content-Type": "application/json",
                    Accept: "application/json, text/plain, */*",
                },
                data: {
                    tenant_id: selectedTenantId,
                    amount: amountNum,
                    description:
                        remarks?.trim() ||
                        `Recharge request of ₹${amountNum.toLocaleString("en-IN")}`,
                    reference_id: `RCHG_${Date.now()}`,
                },
            });

            if (response.data?.success) {
                toast.success(
                    `Recharge request for ₹${amountNum.toLocaleString("en-IN")} submitted successfully!`,
                    { id: toastId }
                );
                setRechargeAmount("");
                setRemarks("");

                // Refresh balance and recharge requests history
                fetchTenantWallet();
                fetchHistory(1);
            } else {
                toast.error(response.data?.message || "Failed to submit recharge request", {
                    id: toastId,
                });
            }
        } catch (error) {
            console.error("Recharge request error:", error);
            toast.error(
                error.response?.data?.message || "Failed to submit recharge request",
                { id: toastId }
            );
        } finally {
            setRechargeSubmitting(false);
        }
    };

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
    };

    // Filter history by tab ('all' | 'pending' | 'resolved' | 'rejected') and search query
    const filteredHistory = useMemo(() => {
        let list = historyData;

        if (activeTab === "pending") {
            list = list.filter((item) => {
                const st = (item.status || "pending").toLowerCase();
                return st === "pending";
            });
        } else if (activeTab === "resolved") {
            list = list.filter((item) => {
                const st = (item.status || "pending").toLowerCase();
                return st === "approved" || st === "resolved";
            });
        } else if (activeTab === "rejected") {
            list = list.filter((item) => {
                const st = (item.status || "pending").toLowerCase();
                return st === "rejected" || st === "declined";
            });
        }

        if (!searchQuery.trim()) return list;
        const query = searchQuery.toLowerCase();
        return list.filter(
            (item) =>
                item.reference_id?.toLowerCase().includes(query) ||
                item.description?.toLowerCase().includes(query) ||
                item.created_by?.toLowerCase().includes(query) ||
                item.status?.toLowerCase().includes(query)
        );
    }, [historyData, activeTab, searchQuery]);

    // Format currency in Indian Rupees format
    const formatINR = (val) => {
        const num = Number(val) || 0;
        return num.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const isLowBalance =
        thresholdBalance != null && currentBalance <= thresholdBalance;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1600px", margin: "0 auto" }}>
            {/* Page Header */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.5, md: 3.5 },
                    mb: 3,
                    borderRadius: 3,
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    background:
                        "radial-gradient(1200px circle at 0% 0%, rgba(59, 130, 246, 0.08), transparent 45%), radial-gradient(900px circle at 100% 0%, rgba(99, 102, 241, 0.06), transparent 45%), #ffffff",
                    boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.03)",
                }}
            >
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                >
                    <Box>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                                sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 8px 16px -4px rgba(37, 99, 235, 0.3)",
                                }}
                            >
                                <FiCreditCard size={24} />
                            </Box>
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}
                                >
                                    Wallet Recharge & Passbook
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                                    Add funds to your account, check real-time balance, and review passbook entries.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Tenant Information Badge */}
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        <Chip
                            label={`${tenantName} (ID: ${selectedTenantId})`}
                            sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(59, 130, 246, 0.08)",
                                color: "#2563eb",
                                borderRadius: 2,
                                px: 1,
                                py: 2,
                            }}
                        />

                        <Tooltip title="Refresh Wallet Balance & History">
                            <IconButton
                                onClick={() => {
                                    fetchTenantWallet();
                                    fetchHistory(pagination.currentPage, activeTab);
                                    toast.success("Wallet refreshed");
                                }}
                                sx={{
                                    bgcolor: "#f1f5f9",
                                    "&:hover": { bgcolor: "#e2e8f0" },
                                    borderRadius: 2,
                                    p: 1.2,
                                }}
                            >
                                <FiRefreshCw className={walletLoading || historyLoading ? "animate-spin" : ""} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>

            {/* Top Grid: Balance & Recharge Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Current Balance Card */}
                <Grid item xs={12} md={5}>
                    <Card
                        elevation={0}
                        sx={{
                            height: "100%",
                            borderRadius: 3,
                            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                            color: "#fff",
                            p: 3,
                            position: "relative",
                            overflow: "hidden",
                            boxShadow: "0 12px 28px -6px rgba(15, 23, 42, 0.25)",
                        }}
                    >
                        {/* Decorative background circles */}
                        <Box
                            sx={{
                                position: "absolute",
                                right: -40,
                                top: -40,
                                width: 200,
                                height: 200,
                                borderRadius: "50%",
                                background: "radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)",
                                pointerEvents: "none",
                            }}
                        />

                        <Stack spacing={2.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box
                                        sx={{
                                            p: 0.8,
                                            borderRadius: 1.5,
                                            bgcolor: "rgba(255, 255, 255, 0.12)",
                                            display: "flex",
                                        }}
                                    >
                                        <FiZap size={18} color="#38bdf8" />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: "#94a3b8", fontWeight: 600 }}>
                                        Current Wallet Balance
                                    </Typography>
                                </Stack>

                                <Chip
                                    size="small"
                                    icon={
                                        isLowBalance ? (
                                            <FiClock color="#f59e0b" />
                                        ) : (
                                            <FiCheckCircle color="#10b981" />
                                        )
                                    }
                                    label={isLowBalance ? "Low Balance" : "Active & Healthy"}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 12,
                                        bgcolor: isLowBalance
                                            ? "rgba(245, 158, 11, 0.15)"
                                            : "rgba(16, 185, 129, 0.15)",
                                        color: isLowBalance ? "#fbbf24" : "#34d399",
                                        borderColor: "transparent",
                                    }}
                                />
                            </Stack>

                            <Box>
                                {walletLoading ? (
                                    <Box sx={{ py: 1 }}>
                                        <CircularProgress size={32} sx={{ color: "#38bdf8" }} />
                                    </Box>
                                ) : (
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 800,
                                            letterSpacing: "-0.03em",
                                            color: "#f8fafc",
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {formatINR(currentBalance)}
                                    </Typography>
                                )}
                                <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5, display: "block" }}>
                                    Available funds for campaigns, payouts & transactions
                                </Typography>
                            </Box>

                            <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)" }} />

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                        Threshold Alert Limit
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#e2e8f0" }}>
                                        {thresholdBalance != null ? formatINR(thresholdBalance) : "Not Configured"}
                                    </Typography>
                                </Grid>
                                {/* <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                        Tenant Identifier
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#e2e8f0" }}>
                                        Slug: {selectedTenantSlug}
                                    </Typography>
                                </Grid> */}
                            </Grid>
                        </Stack>
                    </Card>
                </Grid>

                {/* Recharge Amount Card */}
                <Grid item xs={12} md={7}>
                    <Card
                        elevation={0}
                        sx={{
                            height: "100%",
                            borderRadius: 3,
                            border: "1px solid rgba(226, 232, 240, 0.8)",
                            background: "#ffffff",
                            p: { xs: 2.5, md: 3 },
                            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.04)",
                        }}
                    >
                        <Stack spacing={2.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: "rgba(37, 99, 235, 0.08)",
                                        color: "#2563eb",
                                        display: "flex",
                                    }}
                                >
                                    <FiPlus size={20} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                        Recharge Wallet
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                                        Enter the amount you wish to credit into your tenant wallet.
                                    </Typography>
                                </Box>
                            </Stack>

                            <form onSubmit={handleRechargeSubmit}>
                                <Stack spacing={2.5}>
                                    {/* Amount Input */}
                                    <TextField
                                        label="Recharge Amount"
                                        placeholder="Enter amount (e.g. 5000)"
                                        type="number"
                                        value={rechargeAmount}
                                        onChange={(e) => setRechargeAmount(e.target.value)}
                                        fullWidth
                                        required
                                        autoFocus
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography sx={{ fontWeight: 800, color: "#2563eb", fontSize: "1.2rem" }}>
                                                        ₹
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                        helperText={
                                            rechargeAmount && !Number.isNaN(Number(rechargeAmount))
                                                ? `Requested: ${formatINR(rechargeAmount)} | Projected Balance: ${formatINR(
                                                      currentBalance + Number(rechargeAmount)
                                                  )}`
                                                : "Enter non-negative amount in INR"
                                        }
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                fontSize: "1.1rem",
                                                fontWeight: 700,
                                            },
                                        }}
                                    />

                                    {/* Quick Amount Chips */}
                                    <Box>
                                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, mb: 1, display: "block" }}>
                                            Quick Select Amount:
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                            {QUICK_AMOUNTS.map((amt) => {
                                                const isSelected = Number(rechargeAmount) === amt;
                                                return (
                                                    <Chip
                                                        key={amt}
                                                        label={`+ ₹${amt.toLocaleString("en-IN")}`}
                                                        onClick={() => setRechargeAmount(String(amt))}
                                                        clickable
                                                        color={isSelected ? "primary" : "default"}
                                                        variant={isSelected ? "filled" : "outlined"}
                                                        sx={{
                                                            fontWeight: 700,
                                                            borderRadius: 2,
                                                            transition: "all 0.2s ease",
                                                            "&:hover": {
                                                                bgcolor: isSelected ? "#1d4ed8" : "rgba(37, 99, 235, 0.08)",
                                                                borderColor: "#2563eb",
                                                            },
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    </Box>

                                    {/* Remarks / Reference Notes */}
                                    <TextField
                                        label="Description / Purpose (Optional)"
                                        placeholder="e.g. Monthly marketing budget, Festive campaign allocation"
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        fullWidth
                                        size="small"
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                    />

                                    {/* Action Button */}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={
                                            rechargeSubmitting ||
                                            !rechargeAmount ||
                                            Number(rechargeAmount) <= 0
                                        }
                                        startIcon={
                                            rechargeSubmitting ? (
                                                <CircularProgress size={18} color="inherit" />
                                            ) : (
                                                <FiCreditCard />
                                            )
                                        }
                                        sx={{
                                            py: 1.5,
                                            fontWeight: 800,
                                            fontSize: "1rem",
                                            borderRadius: 2,
                                            textTransform: "none",
                                            background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                                            boxShadow: "0 8px 20px -4px rgba(37, 99, 235, 0.4)",
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                                                boxShadow: "0 10px 24px -4px rgba(37, 99, 235, 0.5)",
                                            },
                                        }}
                                    >
                                        {rechargeSubmitting
                                            ? "Creating Recharge Request..."
                                            : rechargeAmount
                                            ? `Recharge ₹${Number(rechargeAmount).toLocaleString("en-IN")}`
                                            : "Enter Amount to Recharge"}
                                    </Button>
                                </Stack>
                            </form>
                        </Stack>
                    </Card>
                </Grid>
            </Grid>

            {/* Passbook History Section */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(226, 232, 240, 0.8)",
                    bgcolor: "#ffffff",
                    p: { xs: 2.5, md: 3.5 },
                    boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.03)",
                }}
            >
                {/* Passbook Header & Filters */}
                <Stack spacing={2.5}>
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        spacing={2}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                Recharge Requests History
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                                Track all submitted recharge requests. Once accepted by admin, requests are marked as <strong>Resolved</strong> and funds are credited to your balance.
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                            {/* Search Bar */}
                            <TextField
                                size="small"
                                placeholder="Search by ID, desc, user..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiSearch color="#94a3b8" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ minWidth: 240, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                        </Stack>
                    </Stack>

                    {/* Summary Statistics Chips */}
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
                        <Chip
                            icon={<FiClock size={14} color="#d97706" />}
                            label={`Pending Approval: ${pendingRequestsCount}`}
                            sx={{
                                fontWeight: 800,
                                bgcolor: "rgba(245, 158, 11, 0.1)",
                                color: "#b45309",
                                border: "1px solid rgba(245, 158, 11, 0.3)",
                                borderRadius: 2,
                            }}
                        />
                        <Chip
                            icon={<FiCheckCircle size={14} color="#15803d" />}
                            label={`Resolved Requests: ${resolvedRequestsCount}`}
                            sx={{
                                fontWeight: 800,
                                bgcolor: "rgba(34, 197, 94, 0.1)",
                                color: "#15803d",
                                border: "1px solid rgba(34, 197, 94, 0.3)",
                                borderRadius: 2,
                            }}
                        />
                        <Chip
                            icon={<FiXCircle size={14} color="#dc2626" />}
                            label={`Declined Requests: ${rejectedRequestsCount}`}
                            sx={{
                                fontWeight: 800,
                                bgcolor: "rgba(239, 68, 68, 0.1)",
                                color: "#dc2626",
                                border: "1px solid rgba(239, 68, 68, 0.3)",
                                borderRadius: 2,
                            }}
                        />
                        <Chip
                            label={`Total Requests: ${historyData.length}`}
                            sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(15, 23, 42, 0.06)",
                                color: "#475569",
                                borderRadius: 2,
                            }}
                        />
                    </Stack>

                    {/* Filter Tabs: All Requests, Pending, Resolved, and Declined */}
                    <Tabs
                        value={activeTab}
                        onChange={(_, val) => handleTabChange(val)}
                        sx={{
                            borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                minHeight: 48,
                            },
                        }}
                    >
                        <Tab
                            value="all"
                            label={`All Requests (${historyData.length})`}
                            icon={<FiClock />}
                            iconPosition="start"
                        />
                        <Tab
                            value="pending"
                            label={`Pending (${pendingRequestsCount})`}
                            icon={<FiClock color="#d97706" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="resolved"
                            label={`Resolved (${resolvedRequestsCount})`}
                            icon={<FiCheckCircle color="#15803d" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="rejected"
                            label={`Declined (${rejectedRequestsCount})`}
                            icon={<FiXCircle color="#dc2626" />}
                            iconPosition="start"
                        />
                    </Tabs>

                    {/* Recharge Requests Table */}
                    {historyLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                            <CircularProgress size={36} sx={{ color: "#2563eb" }} />
                        </Box>
                    ) : filteredHistory.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 8 }}>
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: "50%",
                                    bgcolor: "#f1f5f9",
                                    color: "#94a3b8",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    margin: "0 auto 16px auto",
                                }}
                            >
                                <FiInfo size={28} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "#334155" }}>
                                No {activeTab !== "all" ? activeTab : ""} recharge requests found
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#94a3b8", mt: 0.5 }}>
                                Once recharge requests are submitted above, their status and approval records will appear here.
                            </Typography>
                        </Box>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: "0 6px" }}>
                                <thead>
                                    <tr style={{ background: "#f8fafc", color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        <th style={{ padding: "12px 16px", border: "none", borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}>Date & Time</th>
                                        <th style={{ padding: "12px 16px", border: "none" }}>Reference ID</th>
                                        <th style={{ padding: "12px 16px", border: "none" }}>Requested Amount</th>
                                        <th style={{ padding: "12px 16px", border: "none" }}>Status</th>
                                        <th style={{ padding: "12px 16px", border: "none" }}>Description</th>
                                        <th style={{ padding: "12px 16px", border: "none" }}>Resolution Info</th>
                                        <th style={{ padding: "12px 16px", border: "none", borderTopRightRadius: 8, borderBottomRightRadius: 8 }}>Created By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHistory.map((item, idx) => {
                                        const isResolved =
                                            (item.status || "").toLowerCase() === "approved" ||
                                            (item.status || "").toLowerCase() === "resolved";
                                        const isRejected =
                                            (item.status || "").toLowerCase() === "rejected" ||
                                            (item.status || "").toLowerCase() === "declined";

                                        return (
                                            <tr
                                                key={item.id || idx}
                                                style={{
                                                    background: isResolved
                                                        ? "rgba(240, 253, 244, 0.55)"
                                                        : isRejected
                                                        ? "rgba(254, 242, 242, 0.55)"
                                                        : "#ffffff",
                                                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                                                    borderRadius: 8,
                                                }}
                                            >
                                                {/* Date & Time */}
                                                <td style={{ padding: "14px 16px", fontWeight: 600, color: "#334155" }}>
                                                    {new Date(item.created_at).toLocaleString("en-IN", {
                                                        dateStyle: "medium",
                                                        timeStyle: "short",
                                                    })}
                                                </td>

                                                {/* Reference ID */}
                                                <td style={{ padding: "14px 16px" }}>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color: "#475569" }}>
                                                            {item.reference_id || item.id || "-"}
                                                        </Typography>
                                                        {item.reference_id && (
                                                          <Tooltip title="Copy reference ID">
                                                              <IconButton
                                                                  size="small"
                                                                  onClick={() => copyToClipboard(item.reference_id)}
                                                                  sx={{ p: 0.5, color: "#94a3b8" }}
                                                              >
                                                                  <FiCopy size={13} />
                                                              </IconButton>
                                                          </Tooltip>
                                                        )}
                                                    </Stack>
                                                </td>

                                                {/* Requested Amount */}
                                                <td style={{ padding: "14px 16px" }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 800,
                                                            fontSize: "1rem",
                                                            color: isResolved ? "#15803d" : isRejected ? "#dc2626" : "#d97706",
                                                        }}
                                                    >
                                                        {formatINR(item.amount)}
                                                    </Typography>
                                                </td>

                                                {/* Status Badge */}
                                                <td style={{ padding: "14px 16px" }}>
                                                    {isResolved ? (
                                                        <Box>
                                                            <Chip
                                                                icon={<FiCheckCircle size={14} color="#15803d" />}
                                                                label="Resolved"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: "rgba(34, 197, 94, 0.15)",
                                                                    color: "#15803d",
                                                                    fontWeight: 800,
                                                                    borderRadius: 1.5,
                                                                    px: 0.5,
                                                                    border: "1px solid rgba(34, 197, 94, 0.3)",
                                                                }}
                                                            />
                                                            <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 600, display: "block", mt: 0.5 }}>
                                                                ✓ Request has been resolved
                                                            </Typography>
                                                        </Box>
                                                    ) : isRejected ? (
                                                        <Box>
                                                            <Chip
                                                                icon={<FiXCircle size={14} color="#dc2626" />}
                                                                label="Declined"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: "rgba(239, 68, 68, 0.15)",
                                                                    color: "#b91c1c",
                                                                    fontWeight: 800,
                                                                    borderRadius: 1.5,
                                                                    px: 0.5,
                                                                    border: "1px solid rgba(239, 68, 68, 0.3)",
                                                                }}
                                                            />
                                                            <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 600, display: "block", mt: 0.5 }}>
                                                                ✕ Request declined by admin
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Chip
                                                                icon={<FiClock size={14} color="#d97706" />}
                                                                label="Pending Approval"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: "rgba(245, 158, 11, 0.15)",
                                                                    color: "#b45309",
                                                                    fontWeight: 800,
                                                                    borderRadius: 1.5,
                                                                    px: 0.5,
                                                                    border: "1px solid rgba(245, 158, 11, 0.3)",
                                                                }}
                                                            />
                                                            <Typography variant="caption" sx={{ color: "#b45309", fontWeight: 600, display: "block", mt: 0.5 }}>
                                                                ⏳ Awaiting admin review
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </td>

                                                {/* Description */}
                                                <td style={{ padding: "14px 16px", maxWidth: 280 }}>
                                                    <Typography variant="body2" sx={{ color: "#334155", fontWeight: 500 }}>
                                                        {item.description || "Recharge request"}
                                                    </Typography>
                                                </td>

                                                {/* Resolution Info */}
                                                <td style={{ padding: "14px 16px" }}>
                                                    {isResolved ? (
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#15803d", fontSize: "0.85rem" }}>
                                                                Credited to Wallet
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                                                                {item.resolved_at
                                                                    ? new Date(item.resolved_at).toLocaleString("en-IN", {
                                                                          dateStyle: "short",
                                                                          timeStyle: "short",
                                                                      })
                                                                    : "Approved"}
                                                            </Typography>
                                                        </Box>
                                                    ) : isRejected ? (
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#dc2626", fontSize: "0.85rem" }}>
                                                                Declined
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                                                                {item.rejection_reason || "Declined by admin"}
                                                                {item.resolved_at && ` • ${new Date(item.resolved_at).toLocaleDateString()}`}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                                                            Under Review
                                                        </Typography>
                                                    )}
                                                </td>

                                                {/* Created By */}
                                                <td style={{ padding: "14px 16px" }}>
                                                    <Chip
                                                        label={item.created_by || "Tenant"}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: "#f1f5f9",
                                                            color: "#475569",
                                                            fontWeight: 600,
                                                            borderRadius: 1.5,
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                            sx={{ pt: 2, borderTop: "1px solid rgba(226, 232, 240, 0.8)" }}
                        >
                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                                Showing page <strong>{pagination.currentPage}</strong> of <strong>{pagination.totalPages}</strong> ({pagination.totalCount} total entries)
                            </Typography>

                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={!pagination.hasPreviousPage}
                                    onClick={() => fetchHistory(pagination.currentPage - 1, activeTab)}
                                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                                >
                                    Previous
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={!pagination.hasNextPage}
                                    onClick={() => fetchHistory(pagination.currentPage + 1, activeTab)}
                                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                                >
                                    Next
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

export default RechargeWallet;